import { motion } from "framer-motion";
import { Check, ArrowUpRight, Trophy, Handshake } from "lucide-react";
import { Chapter, SectionTitle, SectionSub, LovableLogo, EmergentLogo, LOVABLE_URL, EMERGENT_URL, EASE } from "./shared";

const LOVABLE_IF = [
  "You want a beautiful web UI live in minutes",
  "You prefer point-and-click visual editing over prompting",
  "Your scope is web-only: sites, dashboards, MVPs",
  "You love starting from community templates",
];

const EMERGENT_IF = [
  "You need backend, database and auth — not just UI",
  "You want real native mobile apps for iOS & Android",
  "You want AI features like chat, vision or voice built in",
  "You want agents to test, fix and deploy for you",
];

const VerdictCard = ({ side, logo: Logo, title, items, url, accent, testId, btnTestId, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 48 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: EASE }}
    whileHover={{ y: -6 }}
    data-testid={testId}
    className={`glass-panel relative overflow-hidden rounded-3xl p-8 transition-[border-color,box-shadow] duration-500 ${
      accent === "lov"
        ? "hover:border-[#FF2D55]/40 hover:shadow-[0_25px_70px_-20px_rgba(255,45,85,0.35)]"
        : "hover:border-cyan-400/40 hover:shadow-[0_25px_70px_-20px_rgba(34,211,238,0.35)]"
    }`}
  >
    <div
      className={`pointer-events-none absolute -top-24 h-52 w-52 rounded-full blur-[80px] ${
        accent === "lov" ? "-left-24 bg-[#FF2D55]/15" : "-right-24 bg-cyan-400/15"
      }`}
    />
    <div className="relative flex items-center gap-4">
      <Logo size={46} />
      <h3 className="font-display text-xl font-bold text-white sm:text-2xl">{title}</h3>
    </div>
    <ul className="relative mt-7 space-y-4">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-slate-300">
          <span
            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
              accent === "lov" ? "bg-[#FF2D55]/15" : "bg-cyan-400/15"
            }`}
          >
            <Check className={`h-3 w-3 ${accent === "lov" ? "text-[#FF5C8A]" : "text-cyan-300"}`} />
          </span>
          {item}
        </li>
      ))}
    </ul>
    <a
      data-testid={btnTestId}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`relative mt-8 flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold text-white transition-colors duration-300 ${
        accent === "lov"
          ? "border border-[#FF2D55]/40 bg-[#FF2D55]/10 hover:bg-[#FF2D55]/20"
          : "border border-cyan-400/40 bg-cyan-400/10 hover:bg-cyan-400/20"
      }`}
    >
      Visit Official Website
      <ArrowUpRight className="h-4 w-4" />
    </a>
  </motion.div>
);

export default function Verdict() {
  return (
    <section id="verdict" className="relative py-11">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Chapter index="05" label="Final Verdict" testId="verdict-chapter-badge" />
        <SectionTitle>
          So, which one should <span className="text-gradient-cyan">you</span> choose?
        </SectionTitle>
        <SectionSub>
          Both are excellent — for different jobs. Here is the honest split.
        </SectionSub>

        <div className="mt-10 grid gap-8 lg:grid-cols-2">
          <VerdictCard
            side="lov"
            logo={LovableLogo}
            title="Choose Lovable if…"
            items={LOVABLE_IF}
            url={LOVABLE_URL}
            accent="lov"
            testId="verdict-lovable-card"
            btnTestId="verdict-lovable-visit-btn"
            delay={0}
          />
          <VerdictCard
            side="emg"
            logo={EmergentLogo}
            title="Choose Emergent if…"
            items={EMERGENT_IF}
            url={EMERGENT_URL}
            accent="emg"
            testId="verdict-emergent-card"
            btnTestId="verdict-emergent-visit-btn"
            delay={0.12}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-70px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          data-testid="verdict-final-banner"
          className="relative mt-10 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0A0A0A] to-[#12121c] p-8 text-center sm:p-12"
        >
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/70 to-transparent" />
          <div className="pointer-events-none absolute left-1/2 top-0 h-48 w-[560px] -translate-x-1/2 rounded-full bg-blue-600/[0.08] blur-[90px]" />
          <span className="relative mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-cyan-400/30 bg-cyan-400/10 shadow-[0_0_30px_rgba(34,211,238,0.3)]">
            <Trophy className="h-6 w-6 text-cyan-300" />
          </span>
          <h3 className="font-display relative mt-6 text-2xl font-bold text-white sm:text-3xl">
            Our 2026 verdict
          </h3>
          <p className="relative mx-auto mt-4 max-w-3xl text-base leading-relaxed text-slate-400 md:text-lg">
            For pure web design speed and visual polish,{" "}
            <span className="font-semibold text-white">Lovable</span> is unmatched. But for
            shipping complete, revenue-ready products — backend, AI features and native mobile
            included — <span className="font-semibold text-white">Emergent</span> takes the
            overall crown this year.
          </p>
          <div className="relative mt-8 flex flex-wrap items-center justify-center gap-4">
            <a
              data-testid="verdict-final-lovable-btn"
              href={LOVABLE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full max-w-[320px] items-center justify-center gap-2 rounded-full border border-[#FF2D55]/40 bg-[#FF2D55]/10 px-6 py-3 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#FF2D55]/20 sm:w-auto"
            >
              Try Lovable <ArrowUpRight className="h-4 w-4" />
            </a>
            <a
              data-testid="verdict-final-emergent-btn"
              href={EMERGENT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full max-w-[320px] items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-3 text-sm font-bold text-white shadow-[0_0_30px_rgba(59,130,246,0.4)] transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_50px_rgba(34,211,238,0.5)] sm:w-auto"
            >
              Try Emergent <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </motion.div>

        <div
          data-testid="affiliate-disclosure-mobile"
          className="mt-7 rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-sm md:hidden"
        >
          <div className="flex items-start gap-3">
            <Handshake className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
            <p className="text-xs leading-relaxed text-slate-500">
              <span className="font-semibold text-slate-300">Affiliate Disclosure:</span>{" "}
              GrabPerks is an independent review and recommendation platform. We may earn a
              commission when users sign up through links on this page at no additional cost to
              them.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
